import React from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../../components/Common/PageTemplate';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ProgramCard = ({ program }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col group h-full">
            <div
                className="h-48 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${program.image})` }}
            >
                <img
                    src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png"
                    alt="Hacettepe Üniversitesi Logosu"
                    className="absolute top-4 left-4 w-5 h-auto"
                />
            </div>
            <div className={`p-8 flex-grow flex flex-col text-white ${program.bgColor}`}>
                <h3 className="text-2xl font-bold tracking-wide">{program.name}</h3>
                <div className={`w-12 h-1 mt-2 mb-4 ${program.lineColor}`} />
                <p className="text-white/80 mb-6 flex-grow">{t('yandal.card.description')}</p>
                {program.external ? (
                    <a href={program.link} target="_blank" rel="noopener noreferrer" className="self-start mt-auto group">
                        <div className="flex items-center font-bold">
                            {t('yandal.card.viewDepartment')}
                            <div className="ml-3 bg-white/20 group-hover:bg-white transition-colors duration-300 rounded-full p-1">
                                <ArrowRight size={16} className="text-white group-hover:text-black transition-colors duration-300" />
                            </div>
                        </div>
                    </a>
                ) : (
                    <Link to={program.link} className="self-start mt-auto group">
                        <div className="flex items-center font-bold">
                            {t('yandal.card.viewDepartment')}
                            <div className="ml-3 bg-white/20 group-hover:bg-white transition-colors duration-300 rounded-full p-1">
                                <ArrowRight size={16} className="text-white group-hover:text-black transition-colors duration-300" />
                            </div>
                        </div>
                    </Link>
                )}
            </div>
        </div>
    );
};

const YandalPage = () => {
    const { t } = useTranslation();

    const yandalPrograms = [
        {
            name: t('departments.actuarial_science'),
            link: 'https://aktuerya.hacettepe.edu.tr/yandal3.php',
            external: true,
            image: '/aktuerya.jpg',
            bgColor: 'bg-amber-800',
            lineColor: 'bg-amber-400',
            eligible: [
                t('departments.statistics'),
                t('departments.mathematics'),
                t('departments.industrial_engineering'),
                t('departments.economics'),
                t('departments.business_administration')
            ]
        },
        {
            name: t('departments.biology'),
            link: 'https://biology.hacettepe.edu.tr/tr/yan_dal_programi-150',
            external: true,
            image: '/biology.jpg',
            bgColor: 'bg-emerald-800',
            lineColor: 'bg-emerald-400',
            eligible: [t('yandal.eligible.all')]
        },
        {
            name: t('departments.statistics'),
            link: 'https://stat.hacettepe.edu.tr/tr/menu/yan_dal-12',
            external: true,
            image: '/statistic.jpg',
            bgColor: 'bg-sky-800',
            lineColor: 'bg-sky-400',
            eligible: [
                t('departments.actuarial_science'),
                t('departments.biology'),
                t('departments.mathematics'),
                t('departments.computer_engineering'),
                t('departments.industrial_engineering')
            ]
        },
        {
            name: t('departments.chemistry'),
            link: 'https://chem.hacettepe.edu.tr/tr/menu/yandal_programi-131',
            external: true,
            image: '/chemistry.jpg',
            bgColor: 'bg-purple-800',
            lineColor: 'bg-purple-400',
            eligible: []
        },
        {
            name: t('departments.mathematics'),
            link: 'https://mat.hacettepe.edu.tr/yan_dal.html',
            external: true,
            image: '/matematik.jpg',
            bgColor: 'bg-red-800',
            lineColor: 'bg-red-400',
            eligible: [
                t('departments.actuarial_science'),
                t('departments.biology'),
                t('departments.statistics'),
                t('departments.chemistry'),
                t('departments.mathematics_education'),
                t('departments.philosophy'),
                t('departments.economics'),
                t('departments.computer_engineering'),
                t('departments.electrical_electronics_engineering'),
                t('departments.physics_engineering')
            ]
        }
    ];

    return (
        <PageTemplate
            title={t('yandal.title')}
            subtitle={t('yandal.subtitle')}
        >
            <div className="prose lg:prose-xl max-w-none text-gray-800 space-y-6 mb-16">
                <p>{t('yandal.p1')}</p>
                <p>{t('yandal.p2')}</p>
                <p>{t('yandal.p3')}</p>
                <a href="https://fs.hacettepe.edu.tr/oidb/dosyalar/mevzuat/YANDAL%20_web_rehber_09122024.pdf" target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-blue-600 font-bold hover:underline">
                    {t('yandal.guideLink')}
                </a>
            </div>

            <div className="space-y-20">
                {yandalPrograms.map((program, index) => (
                    <motion.div
                        key={index}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.2 }}
                    >
                        <ProgramCard program={program} />
                        <div className="bg-gray-50 rounded-2xl p-8 border">
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">{t('yandal.eligibleDepartments')}</h4>
                            {program.eligible.length === 1 ? (
                                <div className="flex items-center bg-blue-100 text-blue-800 p-4 rounded-lg">
                                    <CheckCircle2 className="w-6 h-6 mr-3 flex-shrink-0" />
                                    <p className="font-semibold">{program.eligible[0]}</p>
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {program.eligible.map((dep, i) => (
                                        <li key={i} className="flex items-start">
                                            <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">{dep}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                ))}
            </div>
        </PageTemplate>
    );
};

export default YandalPage; 